import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, CheckCircle, Clock, AlertCircle, Play, PauseCircle, AlertTriangle } from 'lucide-react';
import teamTasksService from '../../../services/teamTasks.service';

const TeamTasksWidget = ({ userRole, dashboardView = 'team' }) => {
  const [taskData, setTaskData] = useState({
    pending: 0,
    inProgress: 0,
    underReview: 0,
    onHold: 0,
    completed: 0,
    overdue: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTeamTasksData();
  }, [userRole]);

  const fetchTeamTasksData = async () => {
    try {
      setLoading(true);

      // Fetch all team tasks using teamTasksService
      const tasksResponse = await teamTasksService.getTeamTasks({
        view_type: 'all' // Get all team tasks
      });

      if (tasksResponse && tasksResponse.data) {
        const tasks = tasksResponse.data;

        // Count tasks by status
        const counts = {
          pending: 0,
          inProgress: 0,
          underReview: 0,
          onHold: 0,
          completed: 0,
          overdue: 0
        };

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day

        tasks.forEach(task => {
          // Check if task is overdue
          const isOverdue = task.due_date && new Date(task.due_date) < today && task.status !== 'completed';

          if (isOverdue) {
            counts.overdue++;
          } else {
            switch (task.status) {
              case 'pending':
                counts.pending++;
                break;
              case 'in_progress':
                counts.inProgress++;
                break;
              case 'under_review':
                counts.underReview++;
                break;
              case 'on_hold':
                counts.onHold++;
                break;
              case 'completed':
                counts.completed++;
                break;
              default:
                counts.pending++; // Default to pending for unknown statuses
            }
          }
        });

        counts.total = tasks.length;

        setTaskData(counts);
        console.log('📊 Team Tasks Widget - Real-time counts:', counts);
      } else {
        // If no tasks data, set zeros
        setTaskData({
          pending: 0,
          inProgress: 0,
          underReview: 0,
          onHold: 0,
          completed: 0,
          overdue: 0,
          total: 0
        });
      }
    } catch (error) {
      console.error('Error fetching team tasks data:', error);
      // Set zeros on error to avoid showing random numbers
      setTaskData({
        pending: 0,
        inProgress: 0,
        underReview: 0,
        onHold: 0,
        completed: 0,
        overdue: 0,
        total: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWidgetClick = () => {
    // Navigate to appropriate TaskBox team view based on user role
    switch (userRole) {
      case 'admin':
        navigate('/admin/taskbox?view=team');
        break;
      case 'manager':
        navigate('/manager/taskbox?view=team');
        break;
      case 'employee':
        navigate('/employee/taskbox?view=team');
        break;
      default:
        navigate('/tasks/team');
    }
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'inProgress':
        return <Play className="w-4 h-4 text-blue-600" />;
      case 'underReview':
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'onHold':
        return <PauseCircle className="w-4 h-4 text-orange-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Play className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTaskLabel = (type) => {
    switch (type) {
      case 'pending':
        return 'Pending';
      case 'inProgress':
        return 'In Progress';
      case 'underReview':
        return 'Under Review';
      case 'onHold':
        return 'On Hold';
      case 'completed':
        return 'Completed';
      case 'overdue':
        return 'Overdue';
      default:
        return 'Tasks';
    }
  };

  const taskItems = [
    { type: 'pending', count: taskData.pending, color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    { type: 'inProgress', count: taskData.inProgress, color: 'bg-blue-100 text-blue-800 border-blue-200' },
    { type: 'underReview', count: taskData.underReview, color: 'bg-orange-100 text-orange-800 border-orange-200' },
    { type: 'onHold', count: taskData.onHold, color: 'bg-orange-100 text-orange-800 border-orange-200' },
    { type: 'completed', count: taskData.completed, color: 'bg-green-100 text-green-800 border-green-200' },
    { type: 'overdue', count: taskData.overdue, color: 'bg-red-100 text-red-800 border-red-200' }
  ];

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 h-96 cursor-pointer group"
      onClick={handleWidgetClick}
    >
      <div className="p-5 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
              Team Tasks
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Team task overview
            </p>
          </div>
          <div className="bg-green-100 dark:bg-green-900 p-2 rounded-lg group-hover:bg-green-200 dark:group-hover:bg-green-800 transition-colors">
            <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
        </div>

        {/* Total Tasks */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Total Team Tasks</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{taskData.total}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-1 rounded-lg shadow-sm">
              <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        {/* Task Status Items - 3x2 Grid */}
        <div className="grid grid-cols-3 gap-1 flex-1">
          {taskItems.map((item) => (
            <div
              key={item.type}
              className={`flex flex-col items-center justify-center p-1 rounded-lg border ${item.color} hover:shadow-sm transition-all`}
            >
              <div className="flex items-center space-x-1 mb-1">
                {getTaskIcon(item.type)}
                <span className="text-xs font-medium text-center">{getTaskLabel(item.type)}</span>
              </div>
              <span className="text-sm font-bold">{item.count}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">Click to view team tasks</span>
            <svg className="w-3 h-3 text-gray-400 group-hover:text-green-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamTasksWidget;